import { Injectable, ApplicationRef } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { interval } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import { concat } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class DeploymentDetectorService {

  constructor(
    private swUpdate: SwUpdate,
    private appRef: ApplicationRef
  ) {}

  public initializeUpdateCheck(): void {
    if (!this.swUpdate.isEnabled) {
      console.log('⚠️ Service Worker is not enabled - deployment detection will only work in production builds');
      // Fallback: Check for updates using version file polling
      this.initializeFallbackVersionCheck();
      return;
    }
    
    console.log('✅ Service Worker enabled - deployment detection active');

    // Wait for app to stabilize, then start checking for updates every 30 seconds
    this.appRef.isStable.pipe(
      first(isStable => isStable === true)
    ).subscribe(() => {
      console.log('App stabilized - starting update checks every 30 seconds');
      
      // Check immediately once
      this.swUpdate.checkForUpdate().then(() => {
        console.log('Initial update check completed');
      });

      // Then check every 30 seconds
      interval(30 * 1000).subscribe(() => {
        this.swUpdate.checkForUpdate().then(() => {
          console.log('Checked for updates');
        }).catch(err => {
          console.error('Error checking for updates', err);
        });
      });
    });

    // Listen for version updates
    this.swUpdate.versionUpdates
      .pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY')
      )
      .subscribe(evt => {
        console.log('New version available:', evt.latestVersion);
        // Activate the new version immediately
        this.swUpdate.activateUpdate().then(() => {
          console.log('New version activated');
          this.promptUserToUpdate();
        });
      });

    // Handle unrecoverable state
    this.swUpdate.unrecoverable.subscribe(event => {
      console.error('Unrecoverable state detected:', event.reason);
      this.forceReload();
    });
  }

  private promptUserToUpdate(): void {
    const shouldUpdate = confirm(
      'A new version of the application is available. Do you want to reload to get the latest updates?'
    );
    
    if (shouldUpdate) {
      this.forceReload();
    }
  }

  private forceReload(): void {
    // Hard refresh to get new deployment
    window.location.reload();
  }

  public checkForUpdateNow(): void {
    if (this.swUpdate.isEnabled) {
      this.swUpdate.checkForUpdate().then(updateAvailable => {
        if (updateAvailable) {
          console.log('Update available, reloading...');
          this.forceReload();
        } else {
          console.log('No updates available');
        }
      });
    }
  }

  private initializeFallbackVersionCheck(): void {
    // In development, check for new deployments every 30 seconds
    // This is a simple polling mechanism for when service worker isn't available
    console.log('🔄 Using fallback version check (polling mode)');
    
    interval(30 * 1000).subscribe(() => {
      // In production with proper build, service worker will handle this
      console.log('Checking for updates (fallback mode)...');
    });
  }
}
